import { DisplayMarketAsset, Percent } from "@silo/types";
import { Tooltip, TooltipContent, TooltipOrigin } from "../tooltip";
import { FireIcon, HelpIcon, MoneyIcon, SuperfestIcon } from "../../icons";
import { getYieldMetrics, YieldMetrics } from "./utils";
import { cn } from "../../utils/class-names";
import {
  formatDepositApr,
  getDebtRewardsStateColor,
} from "../apr-display-v2/utils";
import { Perk } from "../perk";
import { DepositAprContent } from "./deposit-apr-content";
import { DebtAprContent } from "./debt-apr-content";
import { asPercent } from "@silo/utils";

interface AssetYieldsProps {
  asset: DisplayMarketAsset;
  nativeRewardsSymbol: string;
  revenueApr: Percent;
  dataCy: string;
}

export function AssetYields({
  asset,
  nativeRewardsSymbol,
  revenueApr,
  dataCy,
}: AssetYieldsProps): JSX.Element {
  const { debtPoints, debtMerklRewards, isDebtAprFixed } = asset;

  const yieldMetrics = getYieldMetrics(asset, nativeRewardsSymbol, revenueApr);

  const {
    depositMerklRewards,
    hasDepositRewards,
    depositPoints,
    hasProtectedRevenue,
  } = yieldMetrics;

  const hasRewards = hasDepositRewards || yieldMetrics.hasDebtRewards;

  const allPoints = [...depositPoints, ...debtPoints];
  const pointsKeys = [...new Set(allPoints.map((item) => item._tag))];
  const hasPoints = allPoints.length > 0;
  const allMerklRewards = [...depositMerklRewards, ...debtMerklRewards];
  const hasSuperFest = allMerklRewards.some((reward) => reward.isSuperFest);

  const hasPerks = hasPoints || hasSuperFest;
  const isTooltipOn = hasRewards || hasPerks || hasProtectedRevenue;
  const hasContentSpacer = hasPerks || isDebtAprFixed || hasProtectedRevenue;

  return (
    <Tooltip off={!isTooltipOn}>
      <TooltipOrigin>
        <div
          className={cn(
            "flex flex-col gap-2.5 items-end",
            hasContentSpacer ? "" : "py-3"
          )}
          data-cy={dataCy}
        >
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2.5">
              {isTooltipOn && <HelpIcon className="w-4 h-4 text-shale-300" />}

              <AssetAprs asset={asset} yieldMetrics={yieldMetrics} />
            </div>

            {isDebtAprFixed && (
              <div className="text-xs font-medium text-amber-300">
                Fixed borrow rate
              </div>
            )}

            {hasProtectedRevenue && (
              <div className="text-sm font-medium text-right text-green-400">
                <MoneyIcon className="inline-block -translate-y-[1px]" />{" "}
                Distributing revenue
              </div>
            )}
          </div>

          {hasPerks && (
            <Perk.SignalsRoot>
              {hasSuperFest && <SuperfestIcon className={Perk.iconClassName} />}

              {hasPoints &&
                pointsKeys.map((pointsKey) => (
                  <Perk.PointsIcon
                    className={Perk.iconClassName}
                    pointsKey={pointsKey}
                  />
                ))}
            </Perk.SignalsRoot>
          )}
        </div>
      </TooltipOrigin>

      <TooltipContent>
        <div className="flex flex-col gap-7">
          <DepositAprContent
            asset={asset}
            yieldMetrics={yieldMetrics}
            revenueApr={revenueApr}
          />

          <div className="h-px bg-shale-600"></div>

          <DebtAprContent asset={asset} yieldMetrics={yieldMetrics} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

interface AssetAprsProps {
  asset: DisplayMarketAsset;
  yieldMetrics: YieldMetrics;
}

function AssetAprs({ asset, yieldMetrics }: AssetAprsProps): JSX.Element {
  const { isDebtAprFixed } = asset;

  const {
    displayCollateralYields,
    hasCollateralRewards,
    debtRewardsState,
    totalDebtApr,
    hasProtectedIncentives,
  } = yieldMetrics;

  const hasDepositIncentives = displayCollateralYields
    ? hasCollateralRewards
    : hasProtectedIncentives;

  return (
    <div className="flex items-center gap-1 font-mono text-sm font-medium text-white">
      <span
        data-cy="top"
        className={cn(hasDepositIncentives ? "text-amber-300" : "")}
      >
        {formatDepositApr(yieldMetrics)}
      </span>
      <span>/</span>
      <span
        data-cy="bottom"
        className={cn(
          isDebtAprFixed ? "text-amber-300" : "",
          getDebtRewardsStateColor(debtRewardsState)
        )}
      >
        {asPercent(totalDebtApr)}
      </span>

      {debtRewardsState === "netYield" && (
        <FireIcon className="w-4 h-4 text-green-400" />
      )}
    </div>
  );
}
